<template>
  <div
    class="mb-2 flex justify-between text-base font-bold text-gray-darkest md:text-sm"
  >
    <span>
      <template v-if="activeOption">
        <div class="capitalize lg:inline">
          {{ t('product.options.color') }}: {{ activeOption.title }}
        </div>
      </template>
      <template v-else>
        {{ t('product.options.text_option') }}&nbsp;{{
          t('product.options.color')
        }}
      </template>
    </span>
    <Transition name="fade">
      <span
        v-if="activeOption"
        class="flex items-center gap-1 text-base text-gray-800 before:inline-block before:size-3 before:rounded-full before:content-[''] lg:text-sm"
        :class="activeOption.inStock ? 'before:bg-green' : 'before:bg-pink'"
      >
        <template v-if="activeOption.inStock">
          {{ t('product.stock.text_instock_online') }}
        </template>
        <template v-else>
          {{ t('product.stock.text_oos_online') }}
        </template>
      </span>
    </Transition>
  </div>

  <div class="isolate mb-4 flex w-full justify-start gap-2">
    <GalleryOptionItem
      v-for="(option, index) in optionsOnPage"
      :key="'option-' + option.id"
      :option="option"
      :is-see-more="index + 1 == optionsOnPage.length && restOptionsAmount > 0"
      :rest-options-amount="restOptionsAmount + 1"
      :is-active="
        activeOptionId == option.id &&
        !(index + 1 == optionsOnPage.length && restOptionsAmount > 0)
      "
      class="w-full max-lg:basis-1/5 max-md:basis-1/4 md:max-w-none lg:max-w-18"
      :class="{
        'is-see-more':
          index + 1 == optionsOnPage.length && restOptionsAmount > 0
      }"
      @on-click="onOptionClick"
    />
    <select ref="hiddenOptions" class="hidden" :name="galleryOptionName">
      <option
        :value="activeOptionId || ''"
        :src="activeOption?.image_product_option_value"
        :data-title="activeOption?.name"
      >
        {{ activeOption?.name }}
      </option>
    </select>
  </div>

  <AdaptiveModal
    v-model:active="store.showOptionsModal"
    :modal-props="{
      size: '2xl',
      closeLabel: t('global.accessibility.close'),
      alwaysOnTop: true
    }"
    :drawer-props="{ closeLabel: t('global.accessibility.close') }"
  >
    <GalleryOptionsList
      :active-option-id="activeOptionId"
      :active-option-title="activeOption?.title"
      @on-close="closeModal"
      @on-click="onModalOptionClick"
    />
  </AdaptiveModal>
</template>

<script setup>
import { useWindowSize } from '@vueuse/core';
import { computed, onMounted, ref, watch } from 'vue';

import AdaptiveModal from '@/shared/components/UI/AdaptiveModal.vue';
// import AppModal from '@/shared/components/UI/Legacy/AppModal.vue';
import { useTranslations } from '@/shared/composables/useTranslations.ts';
import { useProductOptionsStore } from '@/shared/pinia/modules/product-options';

import GalleryOptionItem from './GalleryOptionItem.vue';
import GalleryOptionsList from './GalleryOptionsList.vue';

const { width } = useWindowSize();
const { t } = useTranslations();
const optionsOnPage = ref([]);
const hiddenOptions = ref(null);

const store = useProductOptionsStore();

const activeOption = computed(() => store.activeOption);
const activeOptionId = computed(() => store.activeOption?.id);
const blocksNumber = computed(() => store.blocksNumberCalculation(width.value));
const galleryOptionName = computed(
  () => `option[${store.kind?.name.toLowerCase() || 'default'}]`
);

// stock
const optionsInStock = computed(() => store.optionsInStock);
const optionsOutOfStock = computed(() => store.optionsOutOfStock);
const totalOptions = computed(() => [
  ...optionsInStock.value,
  ...optionsOutOfStock.value
]);
const restOptionsAmount = computed(
  () => totalOptions.value.length - optionsOnPage.value.length
);

const onOptionClick = ({ option, isSeeMore }) => {
  if (isSeeMore) {
    store.showOptionsModal = true;
  } else {
    handleVariantSelection(option);
  }
};

const onModalOptionClick = ({ option }) => {
  closeModal();
  handleVariantSelection(option);
};

const handleVariantSelection = (variant) => {
  store.updateActiveOption(variant);
  updateOptionsOnPage();
};

const visibleBlocks = computed(() =>
  Math.min(blocksNumber.value, totalOptions.value.length)
);

const setOptionsPerPage = () => {
  if (optionsInStock.value.length >= visibleBlocks.value) {
    optionsOnPage.value = optionsInStock.value.slice(0, visibleBlocks.value);
  }
  if (optionsInStock.value.length < visibleBlocks.value) {
    const diff = visibleBlocks.value - optionsInStock.value.length;
    const oosOptions = optionsOutOfStock.value.slice(0, diff);
    optionsOnPage.value = [...optionsInStock.value, ...oosOptions];
  }
};

const isActiveOutside = computed(() => {
  if (!activeOptionId.value) {
    return false;
  }

  const activeOptionIndex = optionsOnPage.value.findIndex(
    (option) => option.id == activeOptionId.value
  );

  if (activeOptionIndex < 0) {
    return true;
  }
  if (
    activeOptionIndex + 1 == optionsOnPage.value.length &&
    restOptionsAmount.value > 0
  ) {
    return true;
  }
  return false;
});

const updateOptionsOnPage = () => {
  if (!isActiveOutside.value) return;

  optionsOnPage.value = [
    activeOption.value,
    ...optionsOnPage.value.slice(0, -1)
  ];
};

onMounted(() => {
  optionsOnPage.value = totalOptions.value.slice(0, visibleBlocks.value);

  if (activeOption.value) {
    handleVariantSelection(activeOption.value);
  }
});

const closeModal = () => {
  store.showOptionsModal = false;
};

watch(width, () => {
  setOptionsPerPage();
  updateOptionsOnPage();
});
</script>
